import { useState, useEffect, useMemo } from 'react';
import _, { debounce } from 'underscore';
import { getMatchingBudgetTierBasedOnBudget, CustomBudget } from '../model/constants';

// Selection + validation orchestration shared by BudgetSuggestionContainer (the
// default layout) and BudgetSuggestionTableContainer (the UC table layout).
// Extracted verbatim from BudgetSuggestionContainer so both layouts stay
// behaviorally identical: same auto-select, same 500ms-debounced validate, same
// custom-budget plumbing. Presentation-only DOM concerns (tabster fixes, markup)
// stay in the individual containers.
export const useBudgetSuggestionSelection = ({
  data,
  isLoading,
  mode,
  defaultBudget,
  onBudgetValidate,
  onSelectedBudgetChange,
  initialSelectedBudget,
}) => {
  const [selectedBudget, setSelectedBudget] = useState(initialSelectedBudget || null);

  const [customBudgetResult, setCustomBudgetResult] = useState(null);

  const hasError = !isLoading && _.isEmpty(data);

  const debouncedOnBudgetValidate = useMemo(
    () => debounce(onBudgetValidate, 500),
    [onBudgetValidate]
  );

  // Report the live selected tier immediately (before the 500ms-debounced
  // onBudgetValidate) so opt-in consumers can react to an in-progress tier
  // switch without waiting for validation. Inert for consumers that don't pass
  // onSelectedBudgetChange (defaults to _.noop).
  useEffect(() => {
    onSelectedBudgetChange(selectedBudget);
  }, [selectedBudget, onSelectedBudgetChange]);

  useEffect(() => {
    if (!isLoading && !hasError) {
      if (selectedBudget && selectedBudget !== CustomBudget) {
        const BudgetAmount = data && data[selectedBudget] && data[selectedBudget].BudgetAmountDaily;

        // Only call onBudgetValidate for known tiers that have data (skip injected options like SharedLibrary)
        if (BudgetAmount !== undefined) {
          // Cancel any pending debounced custom budget call to prevent it from overwriting this tier selection
          debouncedOnBudgetValidate.cancel();
          debouncedOnBudgetValidate({ data: { budget: BudgetAmount, budgetTier: selectedBudget }, isValid: true, messages: [] });
        }
      } else if (customBudgetResult) {
        // Call immediately so consumers receive the latest isValid without waiting for the
        // debounce delay. Cancel any pending debounced tier call first to prevent it from
        // overwriting this custom result (reverse of the original PR 6398218 race).
        debouncedOnBudgetValidate.cancel();
        onBudgetValidate({ data: { ...customBudgetResult.data, budgetTier: selectedBudget }, isValid: customBudgetResult.isValid, messages: customBudgetResult.messages });
      }
    }

    if (hasError && customBudgetResult) {
      debouncedOnBudgetValidate.cancel();
      onBudgetValidate({ data: { ...customBudgetResult.data, budgetTier: selectedBudget }, isValid: customBudgetResult.isValid, messages: customBudgetResult.messages });
    }
  }, [customBudgetResult, hasError, data, isLoading, debouncedOnBudgetValidate, onBudgetValidate, selectedBudget]);

  useEffect(() => {
    const matchingTier = getMatchingBudgetTierBasedOnBudget(data, defaultBudget, mode);

    if (selectedBudget === null && !_.isEmpty(data) && defaultBudget) {
      setSelectedBudget(matchingTier);
    }
  }, [defaultBudget, data, selectedBudget, mode]);

  return {
    selectedBudget,
    setSelectedBudget,
    customBudgetResult,
    setCustomBudgetResult,
    hasError,
  };
};
